import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, Output, ViewChild } from '@angular/core';
import { NgForm } from '@angular/forms';
import { Subscription } from 'rxjs';
import { MESSAGECONSTANTS } from 'src/app/core/utils/constants';

// Company-page step 3 (User Management / 4dot5 Admin Contact) - see
// company-page.component.js's `getStepChanges` case 3 for the confirmed
// data-step -> content mapping this component implements: unlike the Goal
// section's original inferred guess, data-step="3" (company-page.html:298-521)
// is one uniform block (4dot5 Admin assignment + password/2FA configuration)
// for every TypeOfCompany, not a Client/BU-specific "early License
// Preferences" view - that content lives under data-step="5" instead.
//
// The legacy `isteven-multi-select` directive (single-selection mode) is
// replaced with the native `ng-select` already used elsewhere in this app
// (e.g. set-job-match-status.component.html) - an AngularJS directive cannot
// run inside a downgraded Angular component's template, same constraint that
// drove the phone-number swap in step 2.
//
// `isPasswordRestrictionsValid`/`complexityRestrictionChange` stay on the
// root legacy controller unchanged: they only read `companyManagement`
// (passed by reference) and jQuery-select checkbox elements by id, both of
// which keep working regardless of which component rendered the DOM.
@Component({
  selector: 'app-company-page-step-users',
  templateUrl: './company-page-step-users.component.html'
})
export class CompanyPageStepUsersComponent implements AfterViewInit, OnDestroy {

  @Input() companyManagement: any = {};
  @Input() companyDetailsUpdateMode: boolean;
  @Input() isFourDotFiveSuperUser: boolean;
  @Input() fourDotFiveAdminsList: any[] = [];
  @Input() selectedFourDotFiveAdmin: any[] = [];
  @Input() step3FormSubmitted: boolean;

  @Output() selectedFourDotFiveAdminChange = new EventEmitter<any[]>();
  @Output() fourDotFiveAdminChosenCallback = new EventEmitter<void>();
  @Output() complexityRestrictionChangeCallback = new EventEmitter<'user' | 'administrator'>();
  @Output() formStateChange = new EventEmitter<any>();

  @ViewChild('company4dot5AdminDetailsForm') company4dot5AdminDetailsForm: NgForm;

  MESSAGECONSTANTS = MESSAGECONSTANTS;

  private subs: Subscription[] = [];

  ngAfterViewInit() {
    setTimeout(() => this.emitFormState());
    if (this.company4dot5AdminDetailsForm) {
      this.subs.push(this.company4dot5AdminDetailsForm.statusChanges.subscribe(() => this.emitFormState()));
    }
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  get selectedAdmin() {
    return this.selectedFourDotFiveAdmin && this.selectedFourDotFiveAdmin.length ? this.selectedFourDotFiveAdmin[0] : null;
  }

  onSelectedAdminChange(admin: any) {
    this.selectedFourDotFiveAdmin = admin ? [admin] : [];
    this.selectedFourDotFiveAdminChange.emit(this.selectedFourDotFiveAdmin);
    this.fourDotFiveAdminChosenCallback.emit();
    if (this.company4dot5AdminDetailsForm) {
      this.company4dot5AdminDetailsForm.form.markAsDirty();
    }
    this.emitFormState();
  }

  onComplexityRestrictionChange(userType: 'user' | 'administrator') {
    this.complexityRestrictionChangeCallback.emit(userType);
  }

  isMinimumLengthGreater(minValue: any, maxValue: any) {
    if (maxValue === 'unlimited') {
      return false;
    }
    return parseInt(minValue, 10) > parseInt(maxValue, 10);
  }

  private emitFormState() {
    const form = this.company4dot5AdminDetailsForm;
    this.formStateChange.emit({
      $valid: form ? form.valid : true,
      $dirty: form ? form.dirty : false,
      $error: { required: form ? form.invalid : false },
      $setDirty: () => form && form.form.markAsDirty(),
      $setPristine: () => form && form.form.markAsPristine()
    });
  }
}
